import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Textarea } from './ui/textarea';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Common memorial/inscription words that should not be flagged
const DEFAULT_DICTIONARY = [
  'remembrance', 'loving', 'beloved', 'dearly', 'cherished', 'treasured',
  'forever', 'memory', 'memories', 'peacefully', 'reunited', 'resting',
  'mum', 'dad', 'nan', 'grandad', 'granny', 'grandma', 'grampa',
  'auntie', 'uncle', 'niece', 'nephew', 'godfather', 'godmother',
  'asleep', 'heaven', 'heavenly', 'angels', 'eternal', 'eternity',
  'goodnight', 'godbless', 'heartfelt', 'unforgettable', 'irreplaceable',
  'xxx', 'xxxx', 'xxxxx'
];

// Simple spell checker using browser's built-in capabilities
const SpellCheckTextarea = ({ 
  value, 
  onChange, 
  placeholder, 
  className,
  rows = 4,
  id,
  'data-testid': dataTestId,
  disabled
}) => {
  const [misspelledWords, setMisspelledWords] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState(null);
  const [suggestionPosition, setSuggestionPosition] = useState({ top: 0, left: 0 });
  const [customDictionary, setCustomDictionary] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const token = localStorage.getItem('token');

  // Load user's custom dictionary
  useEffect(() => {
    const loadCustomDictionary = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/api/user/dictionary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomDictionary(response.data.words || []);
      } catch (error) {
        console.log('Could not load custom dictionary:', error.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadCustomDictionary();
  }, [token]);

  // Simple spell check function
  const checkSpelling = useCallback((text) => {
    if (!text || isLoading) return;

    // Get all words (including hyphenated)
    const words = text.match(/[a-zA-Z'-]+/g) || [];
    const misspelled = [];
    const allDictionary = [...DEFAULT_DICTIONARY, ...customDictionary].map(w => w.toLowerCase());

    words.forEach((word, index) => {
      const lowerWord = word.toLowerCase();
      
      // Skip short words, numbers, and words in custom dictionary
      if (word.length < 3) return;
      if (allDictionary.includes(lowerWord)) return;
      
      // Use a simple check - this relies on common patterns
      // Words that look suspicious (uncommon letter combinations)
      const suspiciousPatterns = [
        /[bcdfghjklmnpqrstvwxyz]{5,}/i, // 5+ consonants in a row
        /(.)\1{2,}/i, // Same letter 3+ times
      ];
      
      // Check against a basic word validation
      // This is a simplified check - in production you'd use a full dictionary
      const isLikelyMisspelled = checkWordValidity(lowerWord);
      
      if (isLikelyMisspelled) {
        // Find position in text
        let position = 0;
        let found = 0;
        for (let i = 0; i < text.length && found <= index; i++) {
          if (text.substring(i).match(new RegExp(`^${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`))) {
            if (found === index) {
              position = i;
              break;
            }
            found++;
          }
          if (/[a-zA-Z'-]/.test(text[i])) {
            // Skip to end of word
            while (i < text.length && /[a-zA-Z'-]/.test(text[i])) i++;
            found++;
            i--;
          }
        }
        
        misspelled.push({
          word,
          index: position,
          suggestions: generateSuggestions(word)
        });
      }
    });

    setMisspelledWords(misspelled);
  }, [customDictionary, isLoading]);

  // Basic word validity check using common English patterns
  const checkWordValidity = (word) => {
    // Common English words - expanded list
    const commonWords = new Set([
      'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'i',
      'it', 'for', 'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at',
      'this', 'but', 'his', 'by', 'from', 'they', 'we', 'say', 'her', 'she',
      'or', 'an', 'will', 'my', 'one', 'all', 'would', 'there', 'their', 'what',
      'so', 'up', 'out', 'if', 'about', 'who', 'get', 'which', 'go', 'me',
      'when', 'make', 'can', 'like', 'time', 'no', 'just', 'him', 'know', 'take',
      'people', 'into', 'year', 'your', 'good', 'some', 'could', 'them', 'see', 'other',
      'than', 'then', 'now', 'look', 'only', 'come', 'its', 'over', 'think', 'also',
      'back', 'after', 'use', 'two', 'how', 'our', 'work', 'first', 'well', 'way',
      'even', 'new', 'want', 'because', 'any', 'these', 'give', 'day', 'most', 'us',
      'love', 'loved', 'loving', 'always', 'never', 'forever', 'rest', 'peace', 'sleep',
      'sleeping', 'heart', 'hearts', 'soul', 'souls', 'miss', 'missed', 'missing',
      'gone', 'but', 'forgotten', 'remember', 'remembered', 'until', 'meet', 'again',
      'wife', 'husband', 'mother', 'father', 'son', 'daughter', 'brother', 'sister',
      'friend', 'friends', 'family', 'life', 'lived', 'lives', 'living', 'death',
      'died', 'passed', 'away', 'home', 'god', 'lord', 'jesus', 'christ', 'amen',
      'bless', 'blessed', 'blessing', 'grace', 'faith', 'hope', 'joy', 'happy',
      'happiness', 'smile', 'smiling', 'laugh', 'laughing', 'cry', 'crying', 'tears',
      'with', 'without', 'within', 'were', 'was', 'are', 'been', 'being', 'had',
      'has', 'have', 'having', 'will', 'would', 'could', 'should', 'may', 'might',
      'must', 'shall', 'can', 'need', 'dare', 'ought', 'used', 'dear', 'dearest',
      'precious', 'special', 'beautiful', 'wonderful', 'amazing', 'incredible',
      'much', 'very', 'too', 'more', 'most', 'less', 'least', 'such', 'own',
      'same', 'different', 'each', 'every', 'both', 'few', 'many', 'some', 'any',
      'other', 'another', 'last', 'next', 'old', 'young', 'long', 'short', 'great',
      'little', 'big', 'small', 'high', 'low', 'early', 'late', 'hard', 'soft',
      'born', 'birth', 'birthday', 'age', 'aged', 'years', 'months', 'days', 'hours',
      'sunrise', 'sunset', 'morning', 'evening', 'night', 'star', 'stars', 'moon', 'sun',
      'sky', 'heaven', 'heavens', 'earth', 'world', 'garden', 'flowers', 'rose', 'roses',
      'tree', 'trees', 'leaves', 'spring', 'summer', 'autumn', 'winter', 'season',
      'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
      'september', 'october', 'november', 'december', 'monday', 'tuesday', 'wednesday',
      'thursday', 'friday', 'saturday', 'sunday', 'today', 'tomorrow', 'yesterday'
    ]);

    if (commonWords.has(word)) return false;
    
    // Check for valid English word patterns
    // Words ending in common suffixes are likely valid
    const validSuffixes = ['ing', 'ed', 'ly', 'er', 'est', 'tion', 'sion', 'ness', 'ment', 'able', 'ible', 'ful', 'less', 'ous', 'ive', 'al', 'ary', 'ory'];
    for (const suffix of validSuffixes) {
      if (word.endsWith(suffix) && word.length > suffix.length + 2) {
        return false; // Likely a valid word with common suffix
      }
    }

    // Check for impossible letter combinations that suggest typos
    const impossiblePatterns = [
      /^[bcdfghjklmnpqrstvwxyz]{4,}/i, // Starting with 4+ consonants
      /[bcdfghjklmnpqrstvwxyz]{5,}/i,  // 5+ consonants anywhere
      /[aeiou]{4,}/i,                   // 4+ vowels in a row
      /(.)\1{2,}/i,                     // Triple letters
      /[qwx][aeiou]{0}[bcdfghjklmnpqrstvwxyz]/i, // Q/W/X not followed by vowel
    ];

    for (const pattern of impossiblePatterns) {
      if (pattern.test(word)) {
        return true; // Likely misspelled
      }
    }

    return false; // Assume valid if no red flags
  };

  // Generate simple suggestions
  const generateSuggestions = (word) => {
    const suggestions = [];
    const lowerWord = word.toLowerCase();
    
    // Common typo fixes
    const commonFixes = {
      'teh': 'the',
      'adn': 'and',
      'hte': 'the',
      'taht': 'that',
      'wiht': 'with',
      'thier': 'their',
      'recieve': 'receive',
      'untill': 'until',
      'occured': 'occurred',
      'seperate': 'separate',
      'definately': 'definitely',
      'occassion': 'occasion',
      'accomodate': 'accommodate',
      'acheive': 'achieve',
      'aquire': 'acquire',
      'arguement': 'argument',
      'begining': 'beginning',
      'beleive': 'believe',
      'calender': 'calendar',
      'catagory': 'category',
      'cemetary': 'cemetery',
      'commited': 'committed',
      'concious': 'conscious',
      'copywrite': 'copyright',
      'embarass': 'embarrass',
      'enviroment': 'environment',
      'existance': 'existence',
      'foriegn': 'foreign',
      'fourty': 'forty',
      'freind': 'friend',
      'goverment': 'government',
      'gaurd': 'guard',
      'happend': 'happened',
      'harrass': 'harass',
      'immediatly': 'immediately',
      'independant': 'independent',
      'intresting': 'interesting',
      'knowlege': 'knowledge',
      'liason': 'liaison',
      'libary': 'library',
      'lisence': 'license',
      'maintainance': 'maintenance',
      'millenium': 'millennium',
      'neccessary': 'necessary',
      'noticable': 'noticeable',
      'occurance': 'occurrence',
      'paralel': 'parallel',
      'passtime': 'pastime',
      'persue': 'pursue',
      'posession': 'possession',
      'prefered': 'preferred',
      'priviledge': 'privilege',
      'profesional': 'professional',
      'publicly': 'publicly',
      'realy': 'really',
      'reccomend': 'recommend',
      'refered': 'referred',
      'relevent': 'relevant',
      'religous': 'religious',
      'restaraunt': 'restaurant',
      'rythm': 'rhythm',
      'sieze': 'seize',
      'succesful': 'successful',
      'suprise': 'surprise',
      'tommorow': 'tomorrow',
      'truely': 'truly',
      'unforseen': 'unforeseen',
      'unfortunatly': 'unfortunately',
      'wierd': 'weird',
      'writting': 'writing'
    };

    if (commonFixes[lowerWord]) {
      suggestions.push(commonFixes[lowerWord]);
    }

    return suggestions.slice(0, 5);
  };

  // Check spelling when value changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      checkSpelling(value);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [value, checkSpelling]);

  // Add word to custom dictionary
  const addToDictionary = async (word) => {
    if (!token) return;
    
    try {
      await axios.post(`${API}/api/user/dictionary`, 
        { word: word.toLowerCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomDictionary(prev => [...prev, word.toLowerCase()]);
      setMisspelledWords(prev => prev.filter(m => m.word.toLowerCase() !== word.toLowerCase()));
      setShowSuggestions(null);
    } catch (error) {
      console.error('Failed to add word to dictionary:', error);
    }
  };

  // Apply suggestion
  const applySuggestion = (misspelled, suggestion) => {
    const newValue = value.substring(0, misspelled.index) + 
                     suggestion + 
                     value.substring(misspelled.index + misspelled.word.length);
    onChange({ target: { value: newValue } });
    setShowSuggestions(null);
  };

  // Handle word click to show suggestions
  const handleWordClick = (e, misspelled) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.target.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    
    setSuggestionPosition({
      top: rect.bottom - containerRect.top + 5,
      left: rect.left - containerRect.left
    });
    setShowSuggestions(misspelled);
  };

  // Render highlighted text
  const renderHighlightedText = () => {
    if (!value || misspelledWords.length === 0) {
      return <span className="invisible whitespace-pre-wrap break-words">{value || placeholder || ' '}</span>;
    }

    const parts = [];
    let lastIndex = 0;

    // Sort misspelled words by index
    const sorted = [...misspelledWords].sort((a, b) => a.index - b.index);

    sorted.forEach((misspelled, i) => {
      // Add text before misspelled word
      if (misspelled.index > lastIndex) {
        parts.push(
          <span key={`text-${i}`} className="invisible">
            {value.substring(lastIndex, misspelled.index)}
          </span>
        );
      }

      // Add misspelled word with highlight
      parts.push(
        <span
          key={`misspelled-${i}`}
          className="relative cursor-pointer"
          onClick={(e) => handleWordClick(e, misspelled)}
        >
          <span className="invisible">{misspelled.word}</span>
          <span 
            className="absolute inset-0 border-b-2 border-red-500 border-dashed bg-red-50/50 rounded"
            style={{ textDecoration: 'underline', textDecorationColor: 'red', textDecorationStyle: 'wavy' }}
          />
        </span>
      );

      lastIndex = misspelled.index + misspelled.word.length;
    });

    // Add remaining text
    if (lastIndex < value.length) {
      parts.push(
        <span key="text-end" className="invisible">
          {value.substring(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Highlight overlay */}
      <div 
        className="absolute inset-0 pointer-events-none overflow-hidden p-3 text-base"
        style={{ 
          fontFamily: 'inherit',
          lineHeight: '1.5',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
      >
        {renderHighlightedText()}
      </div>

      {/* Actual textarea */}
      <Textarea
        ref={textareaRef}
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${className} bg-transparent relative z-10`}
        rows={rows}
        data-testid={dataTestId}
        disabled={disabled}
        spellCheck={false}
      />

      {/* Misspelled word indicator */}
      {misspelledWords.length > 0 && (
        <div className="absolute top-2 right-2 z-20">
          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
            {misspelledWords.length} spelling {misspelledWords.length === 1 ? 'issue' : 'issues'}
          </span>
        </div>
      )}

      {/* Suggestions popup */}
      {showSuggestions && (
        <div 
          className="absolute z-30 bg-white border border-slate-200 rounded-lg shadow-lg p-2 min-w-[200px]"
          style={{ top: suggestionPosition.top, left: suggestionPosition.left }}
        >
          <div className="text-xs text-slate-500 mb-2 pb-2 border-b">
            "{showSuggestions.word}" may be misspelled
          </div>
          
          {showSuggestions.suggestions && showSuggestions.suggestions.length > 0 && (
            <div className="mb-2">
              <div className="text-xs text-slate-400 mb-1">Suggestions:</div>
              {showSuggestions.suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  className="block w-full text-left px-2 py-1 text-sm hover:bg-blue-50 rounded"
                  onClick={() => applySuggestion(showSuggestions, suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
          
          <div className="border-t pt-2 flex gap-2">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={() => addToDictionary(showSuggestions.word)}
            >
              Add to dictionary
            </button>
            <button
              className="text-xs text-slate-500 hover:text-slate-700"
              onClick={() => setShowSuggestions(null)}
            >
              Ignore
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close suggestions */}
      {showSuggestions && (
        <div 
          className="fixed inset-0 z-20" 
          onClick={() => setShowSuggestions(null)}
        />
      )}
    </div>
  );
};

export default SpellCheckTextarea;
